import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import moment from 'moment'
import { useCreateRecurringCreditMutation } from 'graphql/generated'
import { BooksLockedNotice, useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import BuildiumPostingSummary from '../BuildiumPostingSummary'
import { RentalDetailsContext } from '../RentalDetailsContext'
import {
  CreditLiabilityTypeSelect,
  CreditNoteField,
  NonRentCreditWarning
} from './CreditFormFields'

const REACT_APP_RENT_LIABILITY_TYPE_ID = process.env.REACT_APP_RENT_LIABILITY_TYPE_ID!

const IssueRecurringCreditForm: React.FC<
  React.PropsWithChildren<{
    onDirtyChange?: (isDirty: boolean) => void
    onSubmit: () => void | Promise<void>
    rentalId: string
  }>
> = ({ onDirtyChange, onSubmit, rentalId }) => {
  const firstOfNextMonth = moment().add(1, 'months').startOf('month')
  const initialStartDate = firstOfNextMonth.format('YYYY-MM-DD')
  const [totalAmount, setTotalAmount] = useState('')
  const [startDate, setStartDate] = useState(initialStartDate)
  const [numMonths, setNumMonths] = useState(1)
  const [liabilityTypeId, setLiabilityTypeId] = useState(REACT_APP_RENT_LIABILITY_TYPE_ID)
  const [hasConfirmedOverride, setHasConfirmedOverride] = useState(false)
  const [note, setNote] = useState('')
  const [isMoveInCredit, setIsMoveInCredit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [{ fetching, error }, createMoveInCredit] = useCreateRecurringCreditMutation()
  const { liabilityTypes, rentalData } = useContext(RentalDetailsContext)
  const { booksLock, isBooksLockLoading, isBooksLockError, isBuildiumBooksLocked } =
    useBuildiumPostingGuard({
      date: startDate,
      rentalId
    })
  const selectedLiabilityType = liabilityTypes.find(
    (liabilityType) => liabilityType.id === liabilityTypeId
  )
  const numericTotalAmount = Number(totalAmount)
  const isDirty =
    totalAmount !== '' ||
    note.trim().length > 0 ||
    startDate !== initialStartDate ||
    numMonths !== 1 ||
    isMoveInCredit ||
    liabilityTypeId !== REACT_APP_RENT_LIABILITY_TYPE_ID ||
    hasConfirmedOverride
  const occupancyDate = rentalData?.occupancy_date
  const isThisMonthsOccupancyDate =
    Boolean(occupancyDate) &&
    moment(occupancyDate).isSame(moment(), 'month') &&
    moment(startDate).isSame(occupancyDate, 'day')

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const getStartDateError = () => {
    if (!startDate) {
      return 'Start date is required.'
    }

    if (!moment(startDate).isValid()) {
      return 'Enter a valid start date.'
    }

    if (!(moment(startDate).isSameOrAfter(firstOfNextMonth) || isThisMonthsOccupancyDate)) {
      return `Start date must be on or after ${firstOfNextMonth.format('YYYY-MM-DD')}.`
    }

    return null
  }

  const startDateError = getStartDateError()
  const amountError =
    totalAmount !== '' && (!Number.isFinite(numericTotalAmount) || numericTotalAmount <= 0)
      ? 'Enter an amount greater than $0.00.'
      : null
  const numMonthsError = Number(numMonths) <= 0 ? 'Enter at least 1 month.' : null
  const needsLiabilityTypeOverride = liabilityTypeId !== REACT_APP_RENT_LIABILITY_TYPE_ID
  const isValid =
    !startDateError &&
    !amountError &&
    !numMonthsError &&
    Number.isFinite(numericTotalAmount) &&
    numericTotalAmount > 0 &&
    note.trim().length > 0 &&
    Boolean(liabilityTypeId) &&
    !isBooksLockLoading &&
    !isBooksLockError &&
    !isBuildiumBooksLocked &&
    (!needsLiabilityTypeOverride || hasConfirmedOverride)

  const handleSubmit = async () => {
    if (!isValid) {
      return
    }
    setIsSaving(true)
    const result = await createMoveInCredit({
      rental_id: rentalId,
      start_date: startDate,
      num_months: numMonths,
      total_amount: totalAmount,
      notes: note,
      liability_type_id: liabilityTypeId,
      move_in_credit: isMoveInCredit
    })
    setIsSaving(false)
    if (result.error === undefined) {
      setTotalAmount('')
      setStartDate('')
      setNote('')
      await onSubmit()
    }
  }

  const toggleIsMoveInCredit = () => {
    if (isMoveInCredit) {
      setIsMoveInCredit(false)
      setLiabilityTypeId(REACT_APP_RENT_LIABILITY_TYPE_ID)
      setNote('')
    } else {
      setIsMoveInCredit(true)
      setLiabilityTypeId(REACT_APP_RENT_LIABILITY_TYPE_ID)
      setNote('Move-In Credit')
    }
  }

  return (
    <React.Suspense
      fallback={
        <Center py={12}>
          <Spinner />
        </Center>
      }
    >
      <Stack spacing={5}>
        {error !== undefined && (
          <Alert status='error'>
            <AlertIcon />
            {error.message}
          </Alert>
        )}

        <Checkbox
          isChecked={isMoveInCredit}
          onChange={toggleIsMoveInCredit}
          isDisabled={isSaving || fetching}
        >
          This is a move-in credit
        </Checkbox>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
          <FormControl
            isRequired
            isInvalid={Boolean(amountError)}
            isDisabled={isSaving || fetching}
          >
            <FormLabel>Total amount</FormLabel>
            <NumberInput min={0.01} precision={2} value={totalAmount} onChange={setTotalAmount}>
              <NumberInputField placeholder='0.00' />
            </NumberInput>
            <FormErrorMessage>{amountError}</FormErrorMessage>
          </FormControl>

          <FormControl
            isRequired
            isInvalid={Boolean(startDateError) || isBooksLockError || isBuildiumBooksLocked}
            isDisabled={isSaving || fetching}
          >
            <FormLabel>Start date</FormLabel>
            <Input
              type='date'
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
            {startDateError && <FormErrorMessage>{startDateError}</FormErrorMessage>}
          </FormControl>

          <FormControl
            isRequired
            isInvalid={Boolean(numMonthsError)}
            isDisabled={isSaving || fetching}
          >
            <FormLabel>Months</FormLabel>
            <NumberInput
              min={1}
              value={numMonths}
              onChange={(_value, valueAsNumber) => setNumMonths(valueAsNumber || 0)}
            >
              <NumberInputField />
            </NumberInput>
            <FormErrorMessage>{numMonthsError}</FormErrorMessage>
          </FormControl>

          <CreditLiabilityTypeSelect
            isDisabled={isSaving || fetching}
            liabilityTypeId={liabilityTypeId}
            liabilityTypes={liabilityTypes}
            onChange={(nextLiabilityTypeId) => {
              setLiabilityTypeId(nextLiabilityTypeId)
              setHasConfirmedOverride(false)
            }}
          />
        </Grid>

        <BuildiumPostingSummary
          amount={numericTotalAmount}
          date={startDate}
          dateError={startDateError}
          dateLabel='Start'
          entryDescription='Recurring rental credit'
          extraRows={[{ label: 'Months', value: numMonths }]}
          isBooksLockError={isBooksLockError}
          isBooksLockLoading={isBooksLockLoading}
          isBuildiumBooksLocked={isBuildiumBooksLocked}
          lockDate={booksLock?.lockDate}
          typeLabel={selectedLiabilityType?.name}
        />

        {isBooksLockLoading && <Text fontSize='sm'>Checking Buildium books-lock status...</Text>}
        {(isBooksLockError || isBuildiumBooksLocked) && (
          <BooksLockedNotice lockDate={booksLock?.lockDate} isError={isBooksLockError} />
        )}

        {needsLiabilityTypeOverride && (
          <NonRentCreditWarning
            example='E.g. if you are waiving an amenity fee add-on for the first few months, the amenity fee type can be correct.'
            isChecked={hasConfirmedOverride}
            isDisabled={isSaving || fetching}
            onChange={setHasConfirmedOverride}
          />
        )}

        <CreditNoteField isDisabled={isSaving || fetching} note={note} onChange={setNote} />

        <Box display='flex' justifyContent='flex-end'>
          <Button
            colorScheme='teal'
            onClick={handleSubmit}
            isDisabled={!isValid || fetching || isSaving}
            isLoading={fetching || isSaving}
          >
            Issue Recurring Credit
          </Button>
        </Box>
      </Stack>
    </React.Suspense>
  )
}

export default IssueRecurringCreditForm
